import 'antd/dist/antd.less';

// CSS for react-slick
// import '@source/i18next';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import jsCookie from 'js-cookie';
import { ReCaptchaProvider } from 'next-recaptcha-v3';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect, useMemo, useState } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

import ErrorBoundary from '@source/components/ErrorBoundaries';
import MaintenanceProvider from '@source/components/MaintenanceProvider';
import RegionProvider from '@source/components/RegionProvider';
import {
  COOKIE_KEYS,
  WS_USER_ACCESS_TOKEN_KEY,
  WS_USER_CONTACT_ID_KEY,
  WS_USER_PROFILE_COUNTRY_KEY,
  WS_USER_PROFILE_KEY,
  WS_USER_PURCHASE_TOKEN_KEY,
} from '@source/constants/common';
import { commonSeoSchemaOrganization, commonSeoSchemaWebsite } from '@source/constants/seo';
import { FONT_VARIABLES, THEME, THEME_HK_SC, THEME_HK_TC, THEME_JP, THEME_SG_SC } from '@source/constants/theme';
import useGTMEvent from '@source/hooks/useGTMEvent';
import useUserProfileServices from '@source/hooks/useUserProfileServices';
import { TRegionKey } from '@source/interface';
import { IProfileResponsePromise } from '@source/interface/userProfile';
import { wrapper } from '@source/redux';
import { saveReferrer } from '@source/utils/utils';

import '@source/constants/app.less';

import '@design-system/components/BrowsingCard/AgentWarrantyPopover.less';
import '@design-system/components/Tags/Popover.less';

import HeadScript from '@source/components/Vendors/HeadScript';
import { RECAPTCHA_SITE_KEY, SCRIPT } from '@source/constants/script';
import useRegionConfig from '@source/hooks/useRegionConfig';
import '@source/pages/Home/components/CarroCertified/CarroCertified.less';
import '@source/pages/Home/components/CarroCertifiedLite/CarroCertifiedLite.less';
import dynamic from 'next/dynamic';
import { appWithTranslation } from 'next-i18next';
import '../instrument.mjs';

const isShowRegionSelectorAtFirstTimeAccess = false;

const ModalProvider = dynamic(() => import('@source/components/ModalProvider'), {
  ssr: false,
});

const queryClient = new QueryClient();

// Global font styles to apply CSS custom properties with mixed letter/number support
const GlobalFontStyle = createGlobalStyle`
  ${FONT_VARIABLES}
`;

interface GTMObjProps {
  event: string;
  page_url: string;
  page_pathname: string;
  price?: number;
  listing_status?: string;
  make?: string;
  model?: string;
  year?: string;
  body_style?: string;
  transmission?: string;
  fuel_type?: string;
  drivetrain?: string;
  state_of_vehicle?: string;
  user_id?: string;
}

function MyApp({
  Component,
  pageProps,
}: AppProps & {
  Component: AppProps['Component'] & {
    pageReady?: boolean;
  };
}) {
  const { pushGTMEvent } = useGTMEvent();
  const { userProfileState, fetchProfile, setUserFromCookie, saveUserToCookie } = useUserProfileServices();
  const { profile, loaded } = userProfileState;
  const [pageReady, setPageReady] = useState(Component.pageReady ?? true);

  const countryCookie = jsCookie.get(COOKIE_KEYS.STORED_REGION);
  const profileCountry = jsCookie.get(WS_USER_PROFILE_COUNTRY_KEY);
  const userContactId = jsCookie.get(WS_USER_CONTACT_ID_KEY);

  const onPageReady = () => {
    setTimeout(() => {
      setPageReady(true);
    }, 1000);
  };
  const { lang, region, carDetails } = pageProps || {};

  useEffect(() => {
    saveReferrer();

    let gtmObj: GTMObjProps = {
      event: 'PAGE_VIEW',
      page_url: window.location.href,
      page_pathname: window.location.pathname,
    };

    if (carDetails?.carInfoSection?.price && carDetails?.flagSection) {
      const { isAvailable, isPendingSale, isComingSoon, isSold } = carDetails.flagSection;
      gtmObj = {
        ...gtmObj,
        state_of_vehicle: 'used',
        make: carDetails?.inspectionSection?.make,
        model: carDetails?.inspectionSection?.model,
        year: carDetails?.inspectionSection?.yearOfManufacture,
        body_style: carDetails?.overViewSection?.bodyType,
        transmission: carDetails?.overViewSection?.inventory?.transmission,
        fuel_type: carDetails?.overViewSection?.inventory?.fuel_type,
        price: carDetails?.carInfoSection?.price,
        listing_status:
          (isSold && 'sold') ||
          (isPendingSale && 'pending_sale') ||
          (isComingSoon && 'coming_soon') ||
          (isAvailable && 'available') ||
          '',
      };
    }

    if (userContactId) {
      gtmObj = { ...gtmObj, user_id: userContactId };
    }

    pushGTMEvent(gtmObj);
  }, []);

  // Get user information from cookie and save to `userProfile` state when first load app
  useEffect(() => {
    const cookiesJSON = jsCookie.get();

    const cookieProfile =
      typeof cookiesJSON[WS_USER_PROFILE_KEY] === 'string'
        ? JSON.parse(cookiesJSON[WS_USER_PROFILE_KEY])
        : cookiesJSON[WS_USER_PROFILE_KEY];

    // When change country and the previous profile data is not from current country
    if (profileCountry && countryCookie && profileCountry !== countryCookie) {
      jsCookie.remove(WS_USER_PROFILE_KEY);
      jsCookie.remove(WS_USER_ACCESS_TOKEN_KEY);
      jsCookie.remove(WS_USER_PURCHASE_TOKEN_KEY);
      jsCookie.remove(WS_USER_CONTACT_ID_KEY);
      setUserFromCookie();
    }

    if (cookieProfile && profileCountry && countryCookie && profileCountry === countryCookie) {
      setUserFromCookie();
    }
  }, [countryCookie, profileCountry]);

  // The data was saved in above userEffect is not full information (when logged in)
  // Call `fetchProfile` to collect more information and save to `userProfile` state again
  useEffect(() => {
    if (profile?.slug && !loaded) {
      const fetchUserProfile = async () => {
        const res = (await fetchProfile({ country: (countryCookie as TRegionKey) || 'sg', slug: profile?.slug })) as {
          payload: IProfileResponsePromise;
        };
        const { data } = res?.payload || {};

        // eslint-disable-next-line @typescript-eslint/no-unused-vars, camelcase
        if (data) {
          // eslint-disable-next-line camelcase, @typescript-eslint/no-unused-vars
          const { tickets, profile_image, ...needProfileCookieData } = data || {};
          const newProfile = { ...profile, ...needProfileCookieData };
          saveUserToCookie({ profile: newProfile, profile_country: countryCookie });
        }
      };

      fetchUserProfile();
    }
  }, [JSON.stringify(profile), loaded]);

  const theme = useMemo(() => {
    const themeMap = {
      hk: {
        'zh-hans-hk': THEME_HK_SC,
        'zh-hant-hk': THEME_HK_TC,
      },
      my: {
        'zh-my': THEME_HK_SC,
      },
      sg: {
        'zh-sg': THEME_SG_SC,
      },
      jp: {
        '*': THEME_JP,
      },
    };

    const regionThemes = themeMap[region as keyof typeof themeMap];
    if (!regionThemes) return THEME;

    const langKey = lang?.toLowerCase();
    return regionThemes[langKey as keyof typeof regionThemes] || (region === 'jp' && lang !== 'en' ? THEME_JP : THEME);
  }, [lang, region]);

  const {
    countryConfig: { country },
  } = useRegionConfig();

  const { gtmId, gtmAuth, gtmPreview } = SCRIPT?.[country as TRegionKey]?.gtm || {};

  return (
    <>
      <Head>
        {/** https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, shrink-to-fit=no"
        />

        {['sg', 'th', 'id', 'my'].includes(region) && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(commonSeoSchemaWebsite),
            }}
          />
        )}
        {['sg', 'th', 'id', 'my'].includes(region) && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(commonSeoSchemaOrganization),
            }}
          />
        )}
      </Head>
      <main>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <GlobalFontStyle />
            <RegionProvider>
              <ErrorBoundary region={countryCookie as TRegionKey}>
                <MaintenanceProvider region={countryCookie as TRegionKey}>
                  <ReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                    <Component {...pageProps} onPageReady={onPageReady} pageReady={pageReady} />
                  </ReCaptchaProvider>
                </MaintenanceProvider>

                {/* {gtmId && isProduction && userContactId && (
                  <GoogleTagManager gtmId={gtmId} auth={gtmAuth} preview={gtmPreview} />
                )} */}
                <HeadScript />

                {isShowRegionSelectorAtFirstTimeAccess && <ModalProvider />}
              </ErrorBoundary>
            </RegionProvider>
            <ReactQueryDevtools />
          </ThemeProvider>
        </QueryClientProvider>
      </main>
    </>
  );
}

export default wrapper.withRedux(appWithTranslation(MyApp));
